/**
 * Feed screen — the customer-facing home at /app. A vertical list of
 * dossier cards for one category: claim a brand to start outreach, or pass
 * to drop it from future feeds. Utility feed, not a landing page — one
 * accent action per card (Claim), everything else quiet.
 *
 * Category comes from a hardcoded constant for Phase A: there is no
 * category_subscriptions API yet (deferred — the seed writes the row for
 * the discovery pipeline's benefit, but the UI doesn't read it). Once that
 * endpoint exists this becomes a real useCategorySubscriptions() hook.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Badge, Button, Card } from '../../design';
import { handlingRiskLabel, freshness, salesBand, sellerBand, sizeLabel } from '../../lib/bands';
import { useClaimBrand, useFeed, usePassBrand } from '../../lib/feed';
import { AppShell } from '../../components/AppShell';
import { Skeleton } from '../../components/Skeleton';
import { PassDialog } from './PassDialog';
import type { FeedBrand } from '@sourcito/schemas';

const GROCERY = { id: '16310101', label: 'Grocery & Gourmet' };

/** Exported so DossierPage (Task 12) can reuse it without duplicating. */
export const CLAIM_STATUS_LABEL: Record<NonNullable<FeedBrand['claimStatus']>, string> = {
  claimed: 'Claimed',
  contacted: 'Contacted',
  replied: 'Replied',
  account_opened: 'Account opened',
  dropped: 'Dropped',
};

export function FeedPage() {
  const { data, isLoading, isError, error } = useFeed(GROCERY.id);
  const claimBrand = useClaimBrand();
  const passBrand = usePassBrand();
  const [passing, setPassing] = useState<string | null>(null);

  const newCount = data?.brands.filter((b) => b.claimStatus == null).length ?? 0;

  return (
    <AppShell>
      <header className="border-b border-border-subtle">
        <div className="max-w-3xl mx-auto px-8 py-4">
          <h1 className="text-xl font-medium leading-tight">{GROCERY.label}</h1>
          <p className="text-xs text-fg-tertiary">
            {data ? `${newCount} new brand${newCount === 1 ? '' : 's'}` : ' '}
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-8 py-8 space-y-3">
        {isLoading && (
          <div className="space-y-3" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <Card key={i}>
                <Skeleton className="h-4 w-1/3 rounded-xs" />
                <Skeleton className="h-3 w-2/3 rounded-xs mt-3" />
                <Skeleton className="h-3 w-1/4 rounded-xs mt-3" />
              </Card>
            ))}
          </div>
        )}

        {isError && (
          <Card className="border-negative/30 bg-negative/[0.06]">
            <p className="text-sm font-medium text-negative">Failed to load the feed</p>
            <p className="text-sm text-fg-secondary mt-1">
              {(error as Error)?.message ?? 'Unknown error'}
            </p>
          </Card>
        )}

        {data && data.brands.length === 0 && (
          <Card className="text-center py-14">
            <h2 className="text-base font-medium mb-2">No brands yet</h2>
            <p className="text-sm text-fg-secondary max-w-md mx-auto">
              The pipeline is stocking this category. Check back soon.
            </p>
          </Card>
        )}

        {data?.brands.map((brand) => (
          <FeedCard
            key={brand.brandCacheId}
            brand={brand}
            onClaim={() => claimBrand.mutate({ brandCacheId: brand.brandCacheId })}
            onPass={() => setPassing(brand.displayName)}
            claiming={
              claimBrand.isPending && claimBrand.variables?.brandCacheId === brand.brandCacheId
            }
            passing={passBrand.isPending && passBrand.variables?.name === brand.displayName}
            // ApiCallError.message is user-presentable (server error.message or a
            // generic fallback) — same source the query-error card shows above.
            mutationError={
              claimBrand.isError && claimBrand.variables?.brandCacheId === brand.brandCacheId
                ? ((claimBrand.error as Error)?.message ?? 'Claim failed')
                : passBrand.isError && passBrand.variables?.name === brand.displayName
                  ? ((passBrand.error as Error)?.message ?? 'Pass failed')
                  : null
            }
          />
        ))}
        <PassDialog
          brandName={passing ?? ''}
          open={passing != null}
          passing={passBrand.isPending}
          onConfirm={(reason) => {
            if (!passing) return;
            passBrand
              .mutateAsync({ name: passing, reason: reason || undefined })
              .then(() => setPassing(null))
              .catch(() => {});
          }}
          onClose={() => setPassing(null)}
        />
      </main>
    </AppShell>
  );
}

function FeedCard({
  brand,
  onClaim,
  onPass,
  claiming,
  passing,
  mutationError,
}: {
  brand: FeedBrand;
  onClaim: () => void;
  onPass: () => void;
  claiming: boolean;
  passing: boolean;
  mutationError: string | null;
}) {
  const amazonHeavy = brand.amazonShare != null && brand.amazonShare > 0.3;
  // Wholesale vetting (null = not yet checked → show nothing).
  const resellerProof = (brand.independentSellers ?? 0) >= 1 && !brand.brandDirectOnly;
  const doorLikelyClosed = brand.brandDirectOnly === true || brand.cpgParent != null;
  const risks = brand.handlingRisks ?? [];
  return (
    <Card>
      <Link
        to="/app/brands/$brandCacheId"
        params={{ brandCacheId: brand.brandCacheId }}
        className="block group"
      >
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-medium group-hover:underline">{brand.displayName}</span>
          <Badge variant="neutral">{sizeLabel(brand.sizeBucket, brand.catalogSize)}</Badge>
        </div>

        <p className="text-sm text-fg-secondary mt-1">
          {salesBand(brand.velocitySum)} · {sellerBand(Math.round(brand.offerCountAvg ?? 0))} ·{' '}
          {brand.qualifyingCount} products qualify
        </p>

        {amazonHeavy ||
        brand.hasWholesalePage ||
        resellerProof ||
        doorLikelyClosed ||
        risks.length > 0 ? (
          <p className="text-xs mt-1.5 space-x-3">
            {amazonHeavy && (
              <span className="text-warning">
                <span aria-hidden="true">⚠</span> Amazon on many listings
              </span>
            )}
            {brand.hasWholesalePage && (
              <span className="text-positive">
                <span aria-hidden="true">✓</span> has wholesale program
              </span>
            )}
            {resellerProof && (
              <span className="text-positive">
                <span aria-hidden="true">✓</span> sells via independent resellers
              </span>
            )}
            {brand.brandDirectOnly === true && (
              <span className="text-warning">
                <span aria-hidden="true">⚠</span> brand sells it themselves
              </span>
            )}
            {brand.cpgParent != null && (
              <span className="text-warning">
                <span aria-hidden="true">⚠</span> owned by {brand.cpgParent}
              </span>
            )}
            {risks.map((r) => (
              <span key={r} className="text-warning">
                <span aria-hidden="true">⚠</span> {handlingRiskLabel(r)}
              </span>
            ))}
          </p>
        ) : null}
      </Link>

      {mutationError && (
        <p role="alert" className="text-xs text-negative mt-2">
          {mutationError}
        </p>
      )}

      <div className="flex items-center justify-between mt-4">
        {brand.claimStatus == null ? (
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={onClaim} loading={claiming}>
              Claim
            </Button>
            <Button size="sm" variant="ghost" onClick={onPass} loading={passing}>
              Pass
            </Button>
          </div>
        ) : (
          <Badge variant={brand.claimStatus === 'dropped' ? 'neutral' : 'accent'}>
            {CLAIM_STATUS_LABEL[brand.claimStatus]}
          </Badge>
        )}
        <span className="text-xs text-fg-tertiary">{freshness(brand.freshnessAt)}</span>
      </div>
    </Card>
  );
}
